import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Divider,
  Chip,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import {
  NotificationsOff,
  Flag,
  CheckCircleOutline,
  MoreVert,
  ExpandMore,
} from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import NotificationIcon from "./NotificationIcon";
import { getPriorityColor } from "./notificationHelpers";

const NotificationList = ({
  notifications,
  isLoading,
  activeTab,
  onNotificationClick,
  onActionMenuOpen,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  onViewAll,
  hasAnyNotifications,
}) => {
  const theme = useTheme();

  const formatTime = (time) => {
    if (!time) return "";
    try {
      return formatDistanceToNow(new Date(time), { addSuffix: true });
    } catch {
      return "";
    }
  };

  const getSubtitle = (notification) => {
    const parts = [];
    if (notification.client_name) parts.push(notification.client_name);
    if (notification.candidate_name) parts.push(notification.candidate_name);
    return parts.join(" • ");
  };

  return (
    <>
      <Box sx={{ maxHeight: 360, overflow: "auto", p: 0.5 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <NotificationsOff sx={{ fontSize: 40, color: "#94a3b8", opacity: 0.5 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: "0.8rem" }}
            >
              {activeTab === 0 ? "No notifications" : "No unread notifications"}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  disablePadding
                  sx={{
                    borderRadius: 1.5,
                    transition: "0.15s",
                    bgcolor: notification.is_read
                      ? "transparent"
                      : alpha(theme.palette.primary.main, 0.03),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                    },
                  }}
                >
                  <Box
                    onClick={() => onNotificationClick(notification)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.25,
                      py: 1.25,
                      px: 1.5,
                      width: "100%",
                      cursor: "pointer",
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 36, mt: 0.25 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: notification.is_read
                            ? "#f1f5f9"
                            : alpha(getPriorityColor(notification.notification_type), 0.12),
                          color: notification.is_read
                            ? "#94a3b8"
                            : getPriorityColor(notification.notification_type),
                        }}
                      >
                        <NotificationIcon type={notification.notification_type} />
                      </Avatar>
                    </ListItemAvatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 0.75,
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={notification.is_read ? 500 : 700}
                          sx={{
                            fontSize: "0.82rem",
                            color: notification.is_read ? "#475569" : "#0f172a",
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            letterSpacing: 0.1,
                          }}
                        >
                          {notification.notification_type || "Notification"}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                          {notification.is_flagged && (
                            <Flag sx={{ fontSize: 14, color: "#ef4444" }} />
                          )}
                          {notification.is_acknowledged && (
                            <CheckCircleOutline sx={{ fontSize: 14, color: "#22c55e" }} />
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => onActionMenuOpen(e, notification)}
                            sx={{ p: 0.25 }}
                          >
                            <MoreVert sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 400,
                          color: notification.is_read ? "#64748b" : "#334155",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.5,
                          mb: 0.5,
                          letterSpacing: 0.1,
                        }}
                      >
                        {notification.message}
                      </Typography>

                      {getSubtitle(notification) && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.68rem",
                            fontWeight: 500,
                            color: "#64748b",
                            display: "block",
                            mb: 0.5,
                            letterSpacing: 0.15,
                          }}
                        >
                          {getSubtitle(notification)}
                        </Typography>
                      )}

                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.65rem",
                            fontWeight: 400,
                            color: "#94a3b8",
                          }}
                        >
                          {formatTime(notification.created_at)}
                        </Typography>
                        {notification.is_acknowledged && (
                          <Chip
                            label="Acknowledged"
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: "0.58rem",
                              fontWeight: 600,
                              bgcolor: "#dcfce7",
                              color: "#16a34a",
                              borderRadius: 1,
                              "& .MuiChip-label": {
                                px: 0.75,
                              },
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {!notification.is_read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: theme.palette.primary.main,
                          ml: 0.5,
                          mt: 1,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                </ListItem>
                {index < notifications.length - 1 && (
                  <Divider sx={{ opacity: 0.3, mx: 1.5 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {hasNextPage && (
        <Box sx={{ p: 1, borderTop: "1px solid #e2e8f0", textAlign: "center" }}>
          <Button
            variant="text"
            size="small"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            startIcon={
              isFetchingNextPage ? (
                <CircularProgress size={14} />
              ) : (
                <ExpandMore sx={{ fontSize: 18 }} />
              )
            }
            sx={{
              fontSize: "0.72rem",
              fontWeight: 600,
              textTransform: "none",
              color: theme.palette.primary.main,
            }}
          >
            {isFetchingNextPage ? "Loading..." : "Load More"}
          </Button>
        </Box>
      )}

      {hasAnyNotifications && (
        <Box sx={{ p: 0.75, borderTop: "1px solid #e2e8f0", textAlign: "center" }}>
          <Button
            variant="text"
            size="small"
            onClick={onViewAll}
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              textTransform: "none",
              color: theme.palette.primary.main,
            }}
          >
            View All Notifications
          </Button>
        </Box>
      )}
    </>
  );
};

export default NotificationList;